import { connect, DebugEvents, ErrorCode, Events, NatsError } from "nats.ws";

import { natsUrl } from "@/environment";
import { captureExceptionWithContext, captureMessageWithContext } from "@/sentry";
import AuthService from "@/services/auth.service";
import { identityChanged } from "@/sessionIdentity";
import { logoutAndRedirect } from "@/utils";

/**
 * JWT / credential flow for the NATS WebSocket
 * --------------------------------------------
 * `connect()` is called with only `servers: natsUrl` — no explicit token,
 * authenticator or username/password. Authorization is implicit: the browser
 * sends the HttpOnly auth cookie (set by the auth server, same JWT WAMP relies
 * on) with the WebSocket upgrade request to the same origin, and the auth-proxy
 * in front of nats-server validates it. This mirrors the WAMP path in
 * `src/server/index.js`, where autobahn connects with `authmethods: ["anonymous"]`
 * and the cookie carries the JWT.
 *
 * A token refresh therefore means re-issuing that cookie via
 * `AuthService.refreshToken()`; the next (re)connect picks up the fresh cookie.
 */

const connectRetries = 1;

const NATS_ERRORS = {
  AUTH_VIOLATION: "'Authorization Violation'",
  AUTH_EXPIRED: "'User Authentication Expired'",
  AUTH_REVOKED: "'User Authentication Revoked'",
  AUTH_TIMEOUT: "'Authentication Timeout'",
  STALE_CONNECTION: "'Stale Connection'",
};

let connectionPromise = null;
let currentConnection = null;

export function natsConnect({ store } = {}) {
  if (connectionPromise) return connectionPromise;
  connectionPromise = openConnection(store).catch((error) => {
    connectionPromise = null;
    throw error;
  });
  return connectionPromise;
}

async function openConnection(store) {
  const connection = await doConnect({
    servers: natsUrl,
    waitOnFirstConnect: true,
    pingInterval: 10000,
    maxPingOut: 2,
    // must stay finite: exhausting attempts is what resolves closed() and hands over
    // to the refresh/reload/logout path. ~60s when dials fail fast, longer if they hang
    maxReconnectAttempts: 30,
    reconnectTimeWait: 2000,
    name: connectionName(store),
  });
  if (!connection) {
    connectionPromise = null;
    return null;
  }
  currentConnection = connection;
  store?.setNatsConnection(connection);
  store?.connectWebsocket();
  void addConnectionStateHandlers({ connection, store });
  void addCloseConnectionHandlers({ connection, store });
  return connection;
}

function connectionName(store) {
  const { company, id } = store?.user ?? {};
  return `admin:${company ?? "?"}:${id ?? "?"}:${Math.floor(Date.now() / 1000)}`;
}

async function doConnect(options, retries = 0) {
  try {
    return await connect(options);
  } catch (error) {
    if (shouldRefreshToken(error)) {
      await refreshToken();
      return null;
    }
    if (shouldReload(error)) {
      if (retries < connectRetries) {
        return doConnect(options, retries + 1);
      }
      captureExceptionWithContext(error, { connection: "nats" });
      throw error;
    }
    if (shouldLogout(error)) {
      await logoutAndRedirect();
      throw error;
    }
    captureExceptionWithContext(error, { connection: "nats" });
    throw error;
  }
}

export async function closeConnection() {
  const connection = currentConnection;
  // Clear connectionPromise so a later natsConnect() reconnects, but keep
  // currentConnection set: the connection.closed() handler matches on it
  // (`currentConnection === connection`) to run the store cleanup
  // (setNatsConnection(null) + disconnectWebsocket) and null currentConnection.
  connectionPromise = null;
  if (connection && !connection.isClosed()) {
    await connection.close();
  }
}

async function addConnectionStateHandlers({ connection, store }) {
  for await (const status of connection.status()) {
    try {
      handleStatus({ connection, status, store });
    } catch (error) {
      captureExceptionWithContext(error, { connection: "nats" });
    }
  }
}

function handleStatus({ connection, status, store }) {
  switch (status.type) {
    case Events.Disconnect:
    case DebugEvents.StaleConnection:
      store?.disconnectWebsocket();
      break;
    case Events.Reconnect:
      store?.reconnectWebsocket();
      break;
    case Events.Error:
      handleStatusError({ connection, status, store });
      break;
  }
}

function handleStatusError({ connection, status, store }) {
  // status.data is authoritative: lastError can be cleared or overwritten before
  // the queued status event is consumed.
  switch (status.data) {
    case ErrorCode.AuthorizationViolation:
    case ErrorCode.AuthenticationExpired:
    case ErrorCode.AccountExpired:
      // close handler triggers a token refresh/reload or a logout; don't double-log
      return;
    case ErrorCode.PermissionsViolation:
      // lastError is never populated for permission errors
      captureMessageWithContext("NATS permissions violation", {
        connection: connectionContext(connection),
        permission: status.permissionContext ?? {},
      });
      // a flag flip or role change re-mints the cookie on refresh, but this
      // connection keeps using the old subjects until the page reloads
      if (store) void refreshIfIdentityChanged(store);
      return;
  }
  const message = lastError(connection)?.message;
  switch (message) {
    case NATS_ERRORS.AUTH_VIOLATION:
    case NATS_ERRORS.AUTH_EXPIRED:
    case NATS_ERRORS.AUTH_REVOKED:
      return;
    case NATS_ERRORS.STALE_CONNECTION:
      // server-side ping timeout (often a frozen tab that just unfroze); don't log
      return;
    default:
      captureMessageWithContext("NATS connection error", {
        connection: connectionContext(connection),
        error: { message: message ?? String(status.data) },
      });
  }
}

async function addCloseConnectionHandlers({ connection, store }) {
  // nats.ws nulls connection.info once closed, so snapshot before awaiting
  const context = connectionContext(connection);
  const error = await connection.closed();
  if (currentConnection === connection) {
    currentConnection = null;
    connectionPromise = null;
    store?.setNatsConnection(null);
    store?.disconnectWebsocket();
  }
  if (!error) {
    return;
  }
  if (shouldRefreshToken(error)) {
    await refreshToken();
    return;
  }
  if (shouldReload(error)) {
    window.location.reload();
    return;
  }
  if (shouldLogout(error)) {
    void logoutAndRedirect();
    return;
  }
  captureMessageWithContext(`Unhandled NATS connection error: ${error.message}`, {
    connection: context,
    error: { message: error.message },
  });
  if (error instanceof NatsError && error.chainedError?.message) {
    captureExceptionWithContext(error.chainedError, { connection: context });
  }
  void logoutAndRedirect();
}

async function refreshToken() {
  try {
    await AuthService.refreshToken();
    window.location.reload();
  } catch {
    void logoutAndRedirect();
  }
}

let lastViolationRefresh = 0;

// one refresh per window: a burst of denied subjects must not hammer authserver
async function refreshIfIdentityChanged(store) {
  const now = Date.now();
  if (now - lastViolationRefresh < 2 * 60 * 1000) return;
  lastViolationRefresh = now;
  let data;
  try {
    ({ data } = await AuthService.refreshToken());
  } catch (error) {
    // a permissions violation can be an ordinary deny-list hit, so a blip here
    // must not end the session; a really dead one closes and the close handler
    // takes over
    captureExceptionWithContext(error, { connection: "nats" });
    return;
  }
  if (identityChanged(store, data)) window.location.reload();
}

function shouldRefreshToken(error) {
  if (error instanceof NatsError) {
    const refreshCodes = [
      ErrorCode.AuthenticationExpired,
      ErrorCode.AuthorizationViolation,
      ErrorCode.Disconnect,
    ];
    if (refreshCodes.includes(error.code)) {
      return true;
    }
    if (error.isProtocolError() && error.message === NATS_ERRORS.AUTH_REVOKED) {
      return true;
    }
  }
  return false;
}

function shouldReload(error) {
  if (error instanceof NatsError) {
    const reloadMessages = [NATS_ERRORS.AUTH_TIMEOUT, NATS_ERRORS.STALE_CONNECTION];
    if (error.isProtocolError() && reloadMessages.includes(error.message)) {
      return true;
    }
  }
  return false;
}

function shouldLogout(error) {
  return (
    error instanceof NatsError &&
    [ErrorCode.ConnectionRefused, ErrorCode.AccountExpired].includes(error.code)
  );
}

function connectionContext(connection) {
  return {
    name: "nats",
    clientId: connection.info?.client_id,
    connectionName: connection.options?.name,
  };
}

// Reaching into nats internals to read the last protocol error; no public API exists.
function lastError(connection) {
  return connection.protocol?.lastError;
}
